class PracticalSchemaValidator {
  constructor(schemaString) {
    try {
      this.schema = JSON.parse(schemaString);
    } catch (error) {
      console.error('스키마 파싱 오류:', error);
      this.schema = null;
    }
  }
  
  // 중국어 검증 함수
  containsChinese(text) {
    if (!text || typeof text !== 'string') return false;
    
    // 중국어 간체/번체 유니코드 범위
    const chineseRegex = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/;
    return chineseRegex.test(text);
  }
  
  // 메인 검증 함수
  validateDocument(docData) {
    
    // 1. 기본 필수 항목 검증
    const basicResult = this.validateBasicRequiredFields(docData);
    
    // 2. 중국어 검증
    const chineseResult = this.validateChineseContent(docData);
    
    // 3. 스키마 기반 필수 필드 검증
    const schemaResult = this.validateSchemaRequiredFields(docData);
    
    // 4. Select/Dropdown 값 검증
    const dropdownResult = this.validateDropdownValues(docData);
    
    // 5. 물류 특수 규칙 검증
    const logisticsResult = this.validateLogisticsRules(docData);
    
    // 6. 노출속성 중복 검증
    const duplicationResult = this.validateExposedAttributesDuplication();
    
    // 결과 통합
    const combined = this.combineResults([basicResult, chineseResult, schemaResult, dropdownResult, logisticsResult, duplicationResult]);

    // 완성도 계산
    combined.completionRate = this.calculateCompletionRate(docData);

    return combined;
  }
  
  // 중국어 검증
  validateChineseContent(docData) {
    const errors = [];
    
    // 대체 텍스트 중국어 검증
    const altText = docData.imagePage?.details?.altText;
    if (altText && this.containsChinese(altText)) {
      errors.push('이미지 대체 텍스트에 중국어가 포함되어 있습니다.');
    }
    
    // 검색태그 중국어 검증
    const searchTags = docData.productPage?.searchTags;
    if (searchTags) {
      if (typeof searchTags === 'string' && this.containsChinese(searchTags)) {
        errors.push('검색태그에 중국어가 포함되어 있습니다.');
      } else if (Array.isArray(searchTags)) {
        const chineseTagsFound = searchTags.some(tag => this.containsChinese(tag));
        if (chineseTagsFound) {
          errors.push('검색태그에 중국어가 포함되어 있습니다.');
        }
      }
    }
    
    // 상품명 중국어 검증
    const productName = docData.startPage?.productName;
    if (productName && this.containsChinese(productName)) {
      errors.push('상품명에 중국어가 포함되어 있습니다.');
    }
    
    // 상품 설명 중국어 검증
    const productDescription = docData.productPage?.productDescription;
    if (productDescription && this.containsChinese(productDescription)) {
      errors.push('상품 설명에 중국어가 포함되어 있습니다.');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  // 기본 필수 항목 검증 (8개 항목)
  validateBasicRequiredFields(docData) {
    const missingFields = [];
    const errors = [];
    
    // 상품명 (필수)
    if (!this.isValueValid(docData.startPage?.productName)) {
      missingFields.push('startPage.productName');
      errors.push('상품명이 필수입니다.');
    }
    
    // 브랜드명 (필수)
    if (!this.isValueValid(docData.productPage?.brand)) {
      missingFields.push('productPage.brand');
      errors.push('브랜드명이 필수입니다.');
    }
    
    // 공급가 (필수)
    if (!this.isValueValid(docData.productPage?.commonAttributes?.purchasePrice) || 
        docData.productPage?.commonAttributes?.purchasePrice <= 0) {
      missingFields.push('productPage.commonAttributes.purchasePrice');
      errors.push('공급가가 필수입니다.');
    }
    
    // 메인 이미지 (필수)
    if (!this.isValueValid(docData.imagePage?.images?.mainImage)) {
      missingFields.push('imagePage.images.mainImage');
      errors.push('메인 이미지가 필수입니다.');
    }
    
    // 상세 이미지 (필수)
    if (!this.isValueValid(docData.imagePage?.details?.detailedImage)) {
      missingFields.push('imagePage.details.detailedImage');
      errors.push('상세 이미지가 필수입니다.');
    }
    
    // 대체 텍스트 (필수)
    if (!this.isValueValid(docData.imagePage?.details?.altText)) {
      missingFields.push('imagePage.details.altText');
      errors.push('이미지 대체 텍스트가 필수입니다.');
    }
    
    // 검색태그 (필수)
    if (!this.isValueValid(docData.productPage?.searchTags)) {
      missingFields.push('productPage.searchTags');
      errors.push('검색태그가 필수입니다.');
    }
    
    // 노출속성 (최소 1개 필수)
    if (!docData.productPage?.commonAttributes?.exposedAttributes ||
        docData.productPage.commonAttributes.exposedAttributes.length === 0) {
      missingFields.push('productPage.commonAttributes.exposedAttributes');
      errors.push('노출속성이 최소 1개 필요합니다.');
    } else {
      // 노출속성 개별 검증 - 스키마 기반 필수 속성 확인
      const exposedAttrs = docData.productPage.commonAttributes.exposedAttributes;
      const missingRequiredAttrs = [];

      // 스키마에서 필수 노출속성 확인
      if (this.schema?.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes?.allOf) {
        console.log('🔍 [스키마 검증] 노출속성 필수 항목 검사 시작');
        this.schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes.allOf.forEach((config, index) => {
          const attrConfig = config.contains?.properties?.attributeName;
          console.log(`🔍 [스키마 검증] 속성 ${index + 1} 검사:`, {
            mandatory: attrConfig?.mandatory,
            requirement: attrConfig?.requirement,
            attrName: attrConfig?.enum?.[0]
          });

          if (attrConfig?.mandatory === true || attrConfig?.requirement === '필수') {
            const attrName = attrConfig.enum?.[0];
            if (attrName) {
              const hasValidValue = exposedAttrs.some(attr =>
                attr.attributeName === attrName &&
                this.isValueValid(attr.attributeValue)
              );
              console.log(`🔍 [스키마 검증] 필수 속성 "${attrName}" 검사 결과:`, {
                hasValidValue,
                actualValues: exposedAttrs.filter(attr => attr.attributeName === attrName).map(attr => attr.attributeValue)
              });

              if (!hasValidValue) {
                missingRequiredAttrs.push(attrName);
              }
            }
          }
        });
        console.log('🔍 [스키마 검증] 누락된 필수 속성:', missingRequiredAttrs);
      } else {
        console.log('🔍 [스키마 검증] 노출속성 스키마를 찾을 수 없음');
      }

      if (missingRequiredAttrs.length > 0) {
        missingFields.push('productPage.commonAttributes.exposedAttributes');
        errors.push(`필수 노출속성이 누락되었습니다: ${missingRequiredAttrs.join(', ')}`);
      }
    }
    
    return {
      isValid: missingFields.length === 0,
      missingFields,
      errors
    };
  }
  
  // 값 유효성 검사
  isValueValid(value, fieldSchema = null) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    
    // 타입별 추가 검증
    if (fieldSchema?.type === 'integer') {
      return typeof value === 'number' && Number.isInteger(value);
    }
    
    return true;
  }

  // 스키마 기반 필수 필드 검증
  validateSchemaRequiredFields(docData) {
    if (!this.schema) {
      return { isValid: true, missingFields: [], errors: [] };
    }

    const missingFields = [];
    const errors = [];

    // 루트 레벨 required 처리
    if (this.schema.required) {
      this.schema.required.forEach(sectionName => {
        if (sectionName === 'sourcingPage') return; // 제외

        const section = this.schema.properties?.[sectionName];
        if (section?.required) {
          section.required.forEach(fieldName => {
            const fieldSchema = section.properties?.[fieldName];
            const value = this.getNestedValue(docData, `${sectionName}.${fieldName}`);

            if (!this.isValueValid(value, fieldSchema)) {
              // adCert 필드에 대한 특별 처리: 빈 값이어도 경고만 출력
              if (fieldName === 'adCert') {
                console.warn(`adCert 필드가 비어있지만 검증을 통과시킵니다.`);
              } else {
                missingFields.push(`${sectionName}.${fieldName}`);
                errors.push(`${fieldSchema?.title || fieldName}이(가) 필수입니다.`);
              }
            }
          });
        }
      });
    }

    // 스키마 기반 속성별 필수 항목 검증
    this.validateSchemaAttributeRequirements(docData, missingFields, errors);

    return {
      isValid: missingFields.length === 0,
      missingFields,
      errors
    };
  }

  // 스키마 기반 속성 필수 항목 검증
  validateSchemaAttributeRequirements(docData, missingFields, errors) {
    // 노출속성 필수 항목
    const exposedAttrs = this.schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes;
    if (exposedAttrs?.allOf) {
      exposedAttrs.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.mandatory === true) {
          const attrName = attrConfig.enum[0];
          const hasAttr = docData.productPage?.commonAttributes?.exposedAttributes?.some(
            attr => attr.attributeName === attrName && this.isValueValid(attr.attributeValue)
          );

          if (!hasAttr) {
            missingFields.push(`exposedAttributes.${attrName}`);
            errors.push(`필수 노출속성 "${attrConfig.title || attrName}"이(가) 누락되었습니다.`);
          }
        }
      });
    }

    // 비노출속성 필수 항목
    const unexposedAttrs = this.schema.properties?.productPage?.properties?.unexposedAttributes;
    if (unexposedAttrs?.allOf) {
      unexposedAttrs.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.requirement === '필수') {
          const attrName = attrConfig.enum[0];
          const hasAttr = docData.productPage?.unexposedAttributes?.some(
            attr => attr.attributeName === attrName && this.isValueValid(attr.attributeValue)
          );

          if (!hasAttr) {
            missingFields.push(`unexposedAttributes.${attrName}`);
            errors.push(`필수 비노출속성 "${attrConfig.title || attrName}"이(가) 누락되었습니다.`);
          }
        }
      });
    }
  }

  // Select/Dropdown 값 검증
  validateDropdownValues(docData) {
    const errors = [];

    // 속성값 dropdown 검증
    this.validateAttributeDropdowns(docData, errors);

    // 일반 필드 dropdown 검증
    this.validateFieldDropdowns(docData, errors);

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // 속성값 dropdown 검증
  validateAttributeDropdowns(docData, errors) {
    // 노출속성 dropdown 검증
    docData.productPage?.commonAttributes?.exposedAttributes?.forEach(attr => {
      const attrSchema = this.getAttributeSchema('exposedAttributes', attr.attributeName);
      if (attrSchema?.dropdown && attr.attributeValue) {
        if (!attrSchema.dropdown.includes(attr.attributeValue)) {
          errors.push(`노출속성 "${attr.attributeName}": "${attr.attributeValue}"는 허용되지 않는 값입니다.`);
        }
      }
    });

    // 비노출속성 dropdown 검증
    docData.productPage?.unexposedAttributes?.forEach(attr => {
      const attrSchema = this.getAttributeSchema('unexposedAttributes', attr.attributeName);
      if (attrSchema?.dropdown && attr.attributeValue) {
        if (!attrSchema.dropdown.includes(attr.attributeValue)) {
          errors.push(`비노출속성 "${attr.attributeName}": "${attr.attributeValue}"는 허용되지 않는 값입니다.`);
        }
      }
    });
  }

  // 일반 필드 dropdown 검증
  validateFieldDropdowns(docData, errors) {
    // 스키마를 순회하며 dropdown 필드 검증
    this.traverseDocData(docData, (fieldPath, value, fieldSchema) => {
      if (fieldSchema?.dropdown && Array.isArray(fieldSchema.dropdown)) {
        if (value && !fieldSchema.dropdown.includes(value)) {
          // 브랜드 필드는 동적 입력이 가능하므로 드롭다운 검증을 스킵
          if (fieldPath.includes('brand')) {
            console.log(`브랜드 필드는 동적 입력이 허용되므로 드롭다운 검증을 스킵합니다: "${value}"`);
          } else {
            errors.push(`${fieldSchema.title || fieldPath}: "${value}"는 허용되지 않는 값입니다. 허용값: ${fieldSchema.dropdown.join(', ')}`);
          }
        }
      }

      if (fieldSchema?.enum && Array.isArray(fieldSchema.enum)) {
        if (value && !fieldSchema.enum.includes(value)) {
          // 브랜드 필드는 enum 검증도 스킵
          if (fieldPath.includes('brand')) {
            console.log(`브랜드 필드는 동적 입력이 허용되므로 enum 검증을 스킵합니다: "${value}"`);
          } else {
            errors.push(`${fieldSchema.title || fieldPath}: "${value}"는 허용되지 않는 값입니다. 허용값: ${fieldSchema.enum.join(', ')}`);
          }
        }
      }
    });
  }

  // 물류 특수 규칙 검증
  validateLogisticsRules(docData) {
    const errors = [];

    // 포장 사이즈 검증: "150*300*400" 형식 + 최소 크기 검증
    const dimension = docData.logisticsPage?.skuUnitBoxDimension;
    if (dimension) {
      // 형식 검증
      if (!/^\d+\*\d+\*\d+$/.test(dimension)) {
        errors.push('포장 사이즈는 "가로*세로*높이" 형식이어야 합니다. (예: 150*300*400)');
      } else {
        // 최소 크기 검증 (각 면이 10mm 이상, 부피 1000mm³ 이상)
        const [width, height, depth] = dimension.split('*').map(Number);

        // 각 면이 10mm 이상인지 검증
        if (width < 10 || height < 10 || depth < 10) {
          errors.push('포장 사이즈는 각 면이 최소 10mm 이상이어야 합니다. (현재: ' + dimension + ')');
        }

        // 부피가 1000mm³ 이상인지 검증
        const volume = width * height * depth;
        if (volume < 1000) {
          errors.push(`포장 사이즈 부피가 최소 1000mm³ 이상이어야 합니다. (현재: ${volume}mm³)`);
        }
      }
    }

    // 포장 무게 검증: 숫자만
    const weight = docData.logisticsPage?.skuUnitBoxWeight;
    if (weight && !/^\d+$/.test(weight)) {
      errors.push('포장 무게는 숫자만 입력해야 합니다. (단위: 그램)');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // 노출속성 중복 검증
  validateExposedAttributesDuplication() {
    // 단일 문서에서는 중복 검증 불가, 전체 상품 레벨에서 검증 필요
    return { isValid: true, errors: [] };
  }

  // 결과 통합
  combineResults(results) {
    const combined = {
      isValid: true,
      completionRate: 0,
      missingFields: [],
      errors: [],
      warnings: []
    };

    results.forEach(result => {
      if (!result.isValid) {
        combined.isValid = false;
      }

      if (result.missingFields) {
        combined.missingFields.push(...result.missingFields);
      }

      if (result.errors) {
        combined.errors.push(...result.errors);
      }

      if (result.warnings) {
        combined.warnings.push(...result.warnings);
      }
    });

    // 완성도는 validateDocument에서 직접 계산하므로 여기서는 설정하지 않음

    return combined;
  }

  // 완성도 계산
  calculateCompletionRate(docData) {
    const basicResult = this.validateBasicRequiredFields(docData);
    const chineseResult = this.validateChineseContent(docData);
    const schemaResult = this.validateSchemaRequiredFields(docData);

    const totalBasicFields = 8; // 기본 필수 항목 8개
    const completedBasicFields = totalBasicFields - basicResult.missingFields.length;

    const totalSchemaFields = this.getTotalSchemaRequiredFields();
    const completedSchemaFields = totalSchemaFields - schemaResult.missingFields.length;

    // 중국어 검증 점수
    const chineseScore = chineseResult.isValid ? 1 : 0;

    const totalFields = totalBasicFields + totalSchemaFields + 1; // +1은 중국어 검증
    const completedFields = completedBasicFields + completedSchemaFields + chineseScore;

    return totalFields > 0 ? Math.max(0, completedFields / totalFields) : 1.0;
  }

  // 스키마 필수 필드 총 개수 계산
  getTotalSchemaRequiredFields() {
    if (!this.schema) return 0;

    let count = 0;

    // 루트 레벨 required 카운트
    if (this.schema.required) {
      this.schema.required.forEach(sectionName => {
        if (sectionName === 'sourcingPage') return;

        const section = this.schema.properties?.[sectionName];
        if (section?.required) {
          count += section.required.length;
        }
      });
    }

    // 필수 속성 카운트
    count += this.countRequiredAttributes();

    return count;
  }

  // 필수 속성 개수 계산
  countRequiredAttributes() {
    let count = 0;

    // 노출속성 필수 항목
    const exposedAttrs = this.schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes;
    if (exposedAttrs?.allOf) {
      exposedAttrs.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.mandatory === true) {
          count++;
        }
      });
    }

    // 비노출속성 필수 항목
    const unexposedAttrs = this.schema.properties?.productPage?.properties?.unexposedAttributes;
    if (unexposedAttrs?.allOf) {
      unexposedAttrs.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.requirement === '필수') {
          count++;
        }
      });
    }

    return count;
  }

  // 중첩된 값 가져오기
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  // 속성 스키마 가져오기
  getAttributeSchema(attributeType, attributeName) {
    if (!this.schema) return null;

    const attributesSchema = this.schema.properties?.productPage?.properties?.[attributeType];
    if (!attributesSchema?.allOf) return null;

    for (const config of attributesSchema.allOf) {
      const attrConfig = config.contains?.properties?.attributeName;
      if (attrConfig?.enum && attrConfig.enum.includes(attributeName)) {
        return config.contains?.properties?.attributeValue;
      }
    }

    return null;
  }

  // 문서 데이터 순회 (스키마 기반)
  traverseDocData(docData, callback, currentPath = '', currentSchema = null) {
    if (!this.schema) return;

    const schema = currentSchema || this.schema;

    if (schema.properties) {
      Object.keys(schema.properties).forEach(key => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        const prop = schema.properties[key];
        const value = this.getNestedValue(docData, newPath);

        if (prop.type && prop.type !== 'object' && value !== undefined) {
          callback(newPath, value, prop);
        }

        if (prop.type === 'object' && prop.properties) {
          this.traverseDocData(docData, callback, newPath, prop);
        }
      });
    }
  }
}

export default PracticalSchemaValidator;
